/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {
  useCallback,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classnames from 'classnames';
import {
  loadDeviceDetail,
  viewAdminDevice,
  viewDevice
} from '@ion/applications/src/pages/devices/state';
import {translate} from '@ion/core/src/locale/locale';
import {Device, Devices} from '@ion/core/src/types/entities';
import Alert from '@ion/dls/src/components/alert/Alert';
import LinkCell from '@ion/dls/src/components/grid/LinkCell';
import {ExpandCollapseIcon} from '@ion/dls/src/icons/ExpandCollapseIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';

const useStyles = makeStyles(() => ({
  carbRow : {
    height : '20px !important'
  },
  primaryCell : {
    border          : `1px solid ${dlsColor('paleBlue')}`,
    backgroundColor : dlsColor('paleBlue2'),
    fontSize        : 14,
    fontWeight      : 400
  },
  expandIcon : {
    transform  : 'rotate(180deg)',
    transition : 'transform 0.5s ease'
  },
  labelIcon : {
    display : 'flex'
  },
  headerText : {
    color      : dlsColor('grey3'),
    fontSize   : '16px',
    fontFamily : 'Roboto',
    fontWeight : 500
  },
  warningMsg : {
    color      : dlsColor('grey3'),
    fontSize   : '14px',
    fontFamily : 'Roboto',
    fontWeight : 400
  },
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400
  },
  headers : {
    fontSize      : '14px',
    fontWeight    : 600,
    paddingBottom : 10,
    color         : dlsColor('grey2'),
    textTransform : 'uppercase',
    alignItems    : 'center'
  },
  devicesColumn : {
    width : '30%'
  },
  reasonColumn : {
    width : '70%'
  },
  tableContainer : {
    maxHeight : 300,
    overflowY : 'auto'
  },
  thead : {
    position        : 'sticky',
    top             : -1,
    zIndex          : 100,
    backgroundColor : dlsColor('caBlue'),
    border          : `1px solid ${dlsColor('grey5')}`
  }
}));

interface SentinelWarningProps {
  nonSentinelDevices: Devices
};

const columns = ['Unsupported Devices', 'Reason'];

export const SentinelWarning = ({nonSentinelDevices}: SentinelWarningProps) => {
  const classes             = useStyles();
  const dispatch            = useDispatch();
  const [expand, setExpand] = useState(false);

  const onExpand = function() {
    setExpand(prevState => !prevState);
  };

  const onDeviceClick = useCallback((device: Device) => {
    return function() {
      dispatch(viewDevice(device) as any);
      dispatch(loadDeviceDetail(device));
      dispatch(viewAdminDevice(device));
    };
  }, []);
  
  const getNonSentinelTable = function() {
    return (
      <>
        <div className={classes.labelIcon}>
          <LabelTypography variantType={'label1'}
            style={{color: dlsColor('caBlue'), alignContent: 'center'}}>
            {translate(`common:editors.bulkSentinel.${expand ? 'hideLabel' : 'showLabel'}`)}
          </LabelTypography>
          <IconButton style={{paddingBottom: 5}}
            onClick={onExpand} className={expand ? '' : classes.expandIcon}><ExpandCollapseIcon /></IconButton>
        </div>
        <Render.If condition={expand}>
          <TableContainer className={classes.tableContainer}>
            <Table>
              <TableHead className={classes.thead}>
                <TableRow>
                  {columns.map((label) => (
                    <TableCell key={`title-${label}`} className={classes.headers}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {nonSentinelDevices?.map((device) => {
                  return (
                    <TableRow key={`row-${device?.id}`} className={classes.carbRow}>
                      <TableCell className={classnames(classes.primaryCell, classes.devicesColumn)}>
                        <LinkCell data={device}
                          value={device?.id?.toString()}
                          textTransform={'none'}
                          onClick={onDeviceClick(device)} />
                      </TableCell>
                      <TableCell className={classnames(classes.primaryCell, classes.reasonColumn)}>
                        {device?.typeName === 'LMU2650' && device?.sentinelUI !== ''
                          ? translate('common:editors.bulkSentinel.sentinelReason', {id: device?.sentinelUI, typeId: device?.sentinelMobileIdType?.toUpperCase() || ''})
                          : translate('common:editors.bulkSentinel.nonSentinelReason')}
                      </TableCell>
                    </TableRow>
                  );
                })
                }
              </TableBody>
            </Table>
          </TableContainer>
        </Render.If>
      </>
    );
  };
  
  return (
    <>
      <Alert type={'info'}
        message={<>
          <div className={classes.headerText}>{translate('common:editors.bulkSentinel.sentinelTitle')}</div>
          <div className={classes.warningMsg}>{translate('common:editors.bulkSentinel.warningSentinel')}</div>
        </>}
        form={getNonSentinelTable()}/>
    </>
  );
};

export default SentinelWarning;
